// The Information Fields geometry: a subdivision-1 icosphere and a linked,
// slightly irregular hexagonal lattice. Keep stable graph and junction IDs.
type Point3 = [number, number, number]
type Pair = [number, number]
type Face = [number, number, number]

function normalize([x, y, z]: Point3): Point3 {
  const length = Math.hypot(x, y, z) || 1
  return [x / length, y / length, z / length]
}

export function clamp(value: number, minimum = 0, maximum = 1) {
  return Math.min(maximum, Math.max(minimum, value))
}

export function deterministicNoise(value: number) {
  const raw = Math.sin(value * 12.9898 + 78.233) * 43_758.5453
  return raw - Math.floor(raw)
}

function edgeKey(left: number, right: number) {
  return left < right ? `${left}:${right}` : `${right}:${left}`
}

function sortedEdges(keys: Set<string>): Pair[] {
  return [...keys].map((key) => key.split(':').map(Number) as Pair)
    .sort(([a1, b1], [a2, b2]) => a1 - a2 || b1 - b2)
}

export function createSignalSphereGeometry() {
  const golden = (1 + Math.sqrt(5)) / 2
  const points: Point3[] = [
    [-1, golden, 0], [1, golden, 0], [-1, -golden, 0], [1, -golden, 0],
    [0, -1, golden], [0, 1, golden], [0, -1, -golden], [0, 1, -golden],
    [golden, 0, -1], [golden, 0, 1], [-golden, 0, -1], [-golden, 0, 1],
  ]
  const vertices = points.map(normalize)
  const faces: Face[] = [
    [0, 11, 5], [0, 5, 1], [0, 1, 7], [0, 7, 10], [0, 10, 11],
    [1, 5, 9], [5, 11, 4], [11, 10, 2], [10, 7, 6], [7, 1, 8],
    [3, 9, 4], [3, 4, 2], [3, 2, 6], [3, 6, 8], [3, 8, 9],
    [4, 9, 5], [2, 4, 11], [6, 2, 10], [8, 6, 7], [9, 8, 1],
  ]
  const midpoints = new Map<string, number>()
  function midpoint(left: number, right: number) {
    const key = edgeKey(left, right)
    const cached = midpoints.get(key)
    if (cached !== undefined) return cached
    const point = normalize([
      (vertices[left][0] + vertices[right][0]) / 2,
      (vertices[left][1] + vertices[right][1]) / 2,
      (vertices[left][2] + vertices[right][2]) / 2,
    ])
    const index = vertices.push(point) - 1
    midpoints.set(key, index)
    return index
  }
  const subdivided: Face[] = []
  for (const [a, b, c] of faces) {
    const ab = midpoint(a, b)
    const bc = midpoint(b, c)
    const ca = midpoint(c, a)
    subdivided.push([a, ab, ca], [b, bc, ab], [c, ca, bc], [ab, bc, ca])
  }
  const keys = new Set<string>()
  for (const [a, b, c] of subdivided) {
    keys.add(edgeKey(a, b))
    keys.add(edgeKey(b, c))
    keys.add(edgeKey(c, a))
  }
  return { vertices, edges: sortedEdges(keys) }
}

export function projectSpherePoint([x, y, z]: Point3, angle: number) {
  const tilt = -0.205
  const roll = Math.sin(angle * 0.47) * 0.045
  const xAfterY = x * Math.cos(angle) + z * Math.sin(angle)
  const zAfterY = -x * Math.sin(angle) + z * Math.cos(angle)
  const yAfterX = y * Math.cos(tilt) - zAfterY * Math.sin(tilt)
  const zAfterX = y * Math.sin(tilt) + zAfterY * Math.cos(tilt)
  const rotatedX = xAfterY * Math.cos(roll) - yAfterX * Math.sin(roll)
  const rotatedY = xAfterY * Math.sin(roll) + yAfterX * Math.cos(roll)
  const perspective = 1 + zAfterX * 0.085
  return {
    x: 300 + rotatedX * 230.4 * perspective,
    y: 300 + rotatedY * 230.4 * perspective,
    depth: clamp((zAfterX + 1) / 2),
  }
}

export function createHexFieldGeometry(width = 720, height = 520, radius = 39) {
  const horizontalStep = radius * 1.5
  const verticalStep = Math.sqrt(3) * radius
  const vertices: { x: number; y: number; radius: number; opacity: number }[] = []
  const indices = new Map<string, number>()
  const keys = new Set<string>()
  function vertexIndex(x: number, y: number) {
    const key = `${Math.round(x * 1000)}:${Math.round(y * 1000)}`
    const previous = indices.get(key)
    if (previous !== undefined) return previous
    const index = vertices.length
    const seed = index + Math.round(x * 11) + Math.round(y * 17)
    vertices.push({
      x: x + (deterministicNoise(seed) - 0.5) * 4.4,
      y: y + (deterministicNoise(seed + 97) - 0.5) * 4.4,
      radius: 4.9 + deterministicNoise(index + 701) * 0.7,
      opacity: 0.64 + deterministicNoise(index + 811) * 0.3,
    })
    indices.set(key, index)
    return index
  }
  let column = 0
  for (let centerX = -radius * 2; centerX <= width + radius * 2; centerX += horizontalStep, column += 1) {
    const offsetY = column % 2 === 0 ? 0 : verticalStep / 2
    for (let centerY = -radius * 2 + offsetY; centerY <= height + radius * 2; centerY += verticalStep) {
      const polygon: number[] = []
      for (let corner = 0; corner < 6; corner += 1) {
        const angle = corner * Math.PI / 3
        polygon.push(vertexIndex(centerX + radius * Math.cos(angle), centerY + radius * Math.sin(angle)))
      }
      for (let corner = 0; corner < 6; corner += 1) {
        keys.add(edgeKey(polygon[corner], polygon[(corner + 1) % 6]))
      }
    }
  }
  return { width, height, vertices, edges: sortedEdges(keys) }
}

export function annulusFrame(progressInput: number) {
  const progress = clamp(progressInput)
  const eased = progress * progress * (3 - 2 * progress)
  const outerRadius = 18 + eased * (Math.hypot(360, 260) + 78 - 18)
  return {
    outerRadius,
    innerRadius: Math.max(10, outerRadius - 78),
    opacity: clamp(progress / 0.055) * (progress < 0.82 ? 1 : clamp((1 - progress) / 0.18)),
  }
}

export const SPHERE = createSignalSphereGeometry()
export const HEX_FIELD = createHexFieldGeometry()
